import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { startOfWeek, format } from 'date-fns';
import type { CompletionRecord } from '../types/progress';
import type { Child } from '../types/child';

export type GoalType = 'activities' | 'minutes' | 'subjects' | 'streak';

export interface WeeklyGoal {
  id: string;
  childId: string;
  type: GoalType;
  target: number;
  weekStart: string; // YYYY-MM-DD
}

export interface GoalProgress {
  goal: WeeklyGoal;
  current: number;
  percent: number;
}

interface GoalsState {
  goals: WeeklyGoal[];
  setGoals: (childId: string, goals: Omit<WeeklyGoal, 'id' | 'weekStart'>[]) => void;
  getGoalsForChild: (childId: string) => WeeklyGoal[];
  getGoalProgress: (childId: string, completions: CompletionRecord[], child: Child) => GoalProgress[];
}

function currentWeekStart(): string {
  return format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd');
}

export const useGoalsStore = create<GoalsState>()(
  persist(
    (set, get) => ({
      goals: [],

      setGoals: (childId, goalInputs) => {
        const weekStart = currentWeekStart();
        const newGoals: WeeklyGoal[] = goalInputs.map(g => ({
          ...g,
          id: crypto.randomUUID(),
          childId,
          weekStart,
        }));

        set(state => ({
          goals: [
            // Remove existing goals for this child (replace all)
            ...state.goals.filter(g => g.childId !== childId),
            ...newGoals,
          ],
        }));
      },

      getGoalsForChild: (childId) => {
        return get().goals.filter(g => g.childId === childId);
      },

      getGoalProgress: (childId, completions, child) => {
        const goals = get().goals.filter(g => g.childId === childId);
        if (goals.length === 0) return [];

        const weekStart = currentWeekStart();
        const weekCompletions = completions.filter(
          c => c.childId === childId && c.date >= weekStart
        );

        return goals.map(goal => {
          let current = 0;

          switch (goal.type) {
            case 'activities':
              current = weekCompletions.length;
              break;
            case 'minutes':
              current = weekCompletions.reduce((sum, c) => sum + c.durationMinutes, 0);
              break;
            case 'subjects': {
              const uniqueSubjects = new Set(weekCompletions.map(c => c.subject));
              current = uniqueSubjects.size;
              break;
            }
            case 'streak':
              current = child.currentStreak;
              break;
          }

          const percent = goal.target > 0 ? Math.round((current / goal.target) * 100) : 0;

          return { goal, current, percent };
        });
      },
    }),
    { name: 'homeschool-goals', version: 0, migrate: (state) => state }
  )
);
